import React, { useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Bug, Lightbulb, ChatCircleDots, Heart, Star, ChatText } from "@phosphor-icons/react";

const CATS = [
    { v: "bug", label: "Bug / Issue", icon: Bug },
    { v: "feature", label: "Feature request", icon: Lightbulb },
    { v: "general", label: "General feedback", icon: ChatCircleDots },
    { v: "praise", label: "Praise / Thanks", icon: Heart },
];

export default function FeedbackDialog({ trigger }) {
    const [open, setOpen] = useState(false);
    const [category, setCategory] = useState("general");
    const [rating, setRating] = useState(0);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const reset = () => { setCategory("general"); setRating(0); setMessage(""); };

    const submit = async (e) => {
        e.preventDefault();
        if (!rating) {
            toast.error("Please rate your experience");
            return;
        }
        setLoading(true);
        try {
            await api.post("/feedback", { category, rating, message });
            toast.success("Thanks — your feedback was sent to the USC team!");
            reset();
            setOpen(false);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not send feedback");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {trigger || (
                    <button data-testid="open-feedback-dialog-btn" className="text-sm text-stone-600 hover:text-[#990000] inline-flex items-center gap-1.5">
                        <ChatText size={14} /> Feedback
                    </button>
                )}
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]" data-testid="feedback-dialog">
                <DialogHeader>
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#990000] mb-1">Feedback</div>
                    <DialogTitle className="font-heading text-2xl font-semibold text-[#0A0A0A]">Help us improve TrojanTrails</DialogTitle>
                    <DialogDescription className="text-stone-500">
                        Tell us what's working, what's broken, or what you'd love to see next. Read by the USC admin team only.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="space-y-4 pt-2">
                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Type</Label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger data-testid="feedback-category-select" className="mt-2 h-11 border-stone-300">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {CATS.map((c) => (
                                    <SelectItem key={c.v} value={c.v} data-testid={`feedback-category-${c.v}`}>
                                        <div className="flex items-center gap-2">
                                            <c.icon size={16} className="text-[#990000]" />
                                            {c.label}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">How's your experience?</Label>
                        <div className="mt-2 flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    data-testid={`feedback-star-${n}`}
                                    onClick={() => setRating(n)}
                                    onMouseEnter={() => {}}
                                    aria-label={`Rate ${n} of 5`}
                                    className={`w-10 h-10 rounded-md border transition-all flex items-center justify-center ${
                                        n <= rating
                                            ? "bg-[#FFCC00] border-[#FFCC00] text-[#5C0000]"
                                            : "border-stone-200 text-stone-300 hover:border-[#FFCC00] hover:text-[#FFCC00]"
                                    }`}
                                >
                                    <Star size={18} weight={n <= rating ? "fill" : "regular"} />
                                </button>
                            ))}
                            <span className="ml-3 text-xs text-stone-500 font-mono uppercase tracking-wider">
                                {rating ? `${rating} / 5` : "Tap to rate"}
                            </span>
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Your message</Label>
                        <Textarea
                            data-testid="feedback-message-input"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="What happened? What would make TrojanTrails better?"
                            rows={5}
                            maxLength={2000}
                            className="mt-2 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]"
                            required
                        />
                        <div className="text-xs text-stone-400 text-right mt-1">{message.length}/2000</div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="feedback-cancel-btn">
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="feedback-submit-btn"
                            className="bg-[#990000] hover:bg-[#7A0000] text-white"
                        >
                            {loading ? "Sending…" : "Send feedback"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
